import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ProductService from '@/services/CatalogServices/ProductService';
import { extractError } from '@/helpers/extractApiError';

const model = {
    data: [],
    totalItems: 0,
    loading: false,
    error: null,
    successOnStatusChange: '',
    successOnAddProductsToCategories: '',
    xlsxUrl: null,
    loadingExport: false,
};

export const getProductList = createAsyncThunk(
    'product/getProductList',
    async (data, { rejectWithValue }) => {
        try {
            const response = await ProductService.getProductList(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const changeProductStatus = createAsyncThunk(
    'product/changeProductStatus',
    async (data, { rejectWithValue }) => {
        try {
            const response = await ProductService.changeProductStatus(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const addProductsToCategories = createAsyncThunk(
    'product/addProductsToCategories',
    async (data, { rejectWithValue }) => {
        try {
            const response = await ProductService.addProductsToCategories(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const exportProducts = createAsyncThunk(
    'product/exportProducts',
    async (data, { rejectWithValue }) => {
        try {
            const response = await ProductService.exportProducts(data);
            const { file_url: xlsxUrl } = response.data;
            return xlsxUrl;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

const productListSlice = createSlice({
    name: 'productList',
    initialState: model,
    reducers: {
        resetChangeProductStatusStateOnSuccess: (state) => {
            state.successOnStatusChange = '';
        },
        resetChangeProductStatusStateOnError: (state) => {
            state.error = null;
        },
        resetAddProductsToCategoriesStateOnSuccess: (state) => {
            state.successOnAddProductsToCategories = '';
        },
        resetAddProductsToCategoriesStateOnError: (state) => {
            state.error = null;
        },
        resetExport: (state) => {
            state.xlsxUrl = null;
        },
    },
    extraReducers: (builder) => {
        // product list
        builder.addCase(getProductList.pending, (state) => {
            state.loading = true;
        }).addCase(getProductList.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload.products;
            state.totalItems = action.payload.total;
        }).addCase(getProductList.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // change product status
        builder.addCase(changeProductStatus.pending, (state) => {
            state.loading = true;
        }).addCase(changeProductStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.successOnStatusChange = 'Product status changed successfully!';
            state.data = state.data.map((product) => {
                if (action.payload.product_ids.includes(product.id)) {
                    return { ...product, publish_status: action.payload.publish_status };
                }
                return product;
            });
        }).addCase(changeProductStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // add products to category
        builder.addCase(addProductsToCategories.pending, (state) => {
            state.loading = true;
        }).addCase(addProductsToCategories.fulfilled, (state) => {
            state.loading = false;
            state.successOnAddProductsToCategories = 'Products added to category successfully!';
        }).addCase(addProductsToCategories.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // export products
        builder.addCase(exportProducts.pending, (state) => {
            state.loadingExport = true;
        });
        builder.addCase(exportProducts.fulfilled, (state, action) => {
            state.loadingExport = false;
            state.xlsxUrl = action.payload;
        });
        builder.addCase(exportProducts.rejected, (state, action) => {
            state.loadingExport = false;
            state.error = extractError(action);
        });
    },
});

export const {
    resetChangeProductStatusStateOnSuccess,
    resetChangeProductStatusStateOnError,
    resetAddProductsToCategoriesStateOnSuccess,
    resetAddProductsToCategoriesStateOnError,
    resetExport,
} = productListSlice.actions;

export default productListSlice.reducer;
